import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeploymentGroups, useDeleteDeploymentGroup } from '../../services/queries';
import { LoadingSpinner, EmptyState, ConfirmDialog } from '../common';
import { ErrorAlert } from '../ErrorAlert';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { DeploymentGroupDto } from '../../types/pipelineFlow';

export function DeploymentGroupsList() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: deploymentGroups, isLoading, error } = useDeploymentGroups();
  const errorHandler = useErrorHandler();

  // Handle errors with our error handler
  if (error && !errorHandler.isVisible) {
    errorHandler.showError(error);
  }

  const filteredGroups = deploymentGroups?.filter((group: DeploymentGroupDto) =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorAlert 
        error={errorHandler.error} 
        isVisible={errorHandler.isVisible} 
        onClose={errorHandler.hideError}
      />
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">Deployment Groups</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage deployment groups and their associated environments.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <Link
            to="/admin/deployment-groups/new"
            className="block rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Create Group
          </Link>
        </div>
      </div>

      <div className="mt-8">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search deployment groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input max-w-md"
          />
        </div>

        {filteredGroups.length === 0 ? (
          <EmptyState
            title="No deployment groups found"
            description={searchTerm ? "Try adjusting your search terms." : "Create your first deployment group to get started."}
            action={searchTerm ? undefined : {
              label: "Create Group",
              onClick: () => window.location.href = "/admin/deployment-groups/new"
            }}
            icon={
              <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125S3.75 19.903 3.75 17.625V6.375" />
              </svg>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredGroups.map((group: DeploymentGroupDto) => (
              <DeploymentGroupCard key={group.id} group={group} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DeploymentGroupCardProps {
  group: DeploymentGroupDto;
}

function DeploymentGroupCard({ group }: DeploymentGroupCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteDeploymentGroup = useDeleteDeploymentGroup();
  const errorHandler = useErrorHandler();

  const handleDelete = async () => {
    try {
      await deleteDeploymentGroup.mutateAsync(group.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      errorHandler.showError(error instanceof Error ? error : new Error('Failed to delete deployment group'));
    }
  };

  return (
    <>
      <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <Link
              to={`/admin/deployment-groups/${group.id}`}
              className="text-lg font-semibold text-gray-900 hover:text-blue-600"
            >
              {group.name}
            </Link>
            <p className="mt-1 text-sm text-gray-500">
              Created {new Date(group.createdOn).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
          <div className="flex items-center">
            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {group.pipelineCount} pipelines
          </div>
          <div className="flex items-center text-xs">
            Last modified {new Date(group.lastModifiedOn).toLocaleDateString()}
          </div>
        </div>

        <div className="mt-4 flex justify-end items-center space-x-3">
          <Link
            to={`/admin/deployment-groups/${group.id}/edit`}
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Edit
          </Link>
          <Link
            to={`/admin/deployment-groups/${group.id}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View Details
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-ghost-danger"
            disabled={deleteDeploymentGroup.isPending}
          >
            {deleteDeploymentGroup.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Deployment Group"
        message={`Are you sure you want to delete "${group.name}"? This action cannot be undone and will also delete all pipelines within this group.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive={true}
      />

      {errorHandler.isVisible && (
        <ErrorAlert 
          error={errorHandler.error} 
          isVisible={errorHandler.isVisible} 
          onClose={errorHandler.hideError}
        />
      )}
    </>
  );
}